import { FC, useContext, useEffect, useState } from 'react';

import { FetchResult } from '@apollo/client';
import { Button, FormField, Input, Modal, Toggle } from '@smartshell/ui-kit';
import { useForm, Controller } from 'react-hook-form';

import { LocalizationContext } from 'localization';
import { ISetSettingResponse } from 'settings';
import { IAutorunApplication } from 'types';

import s from './ManageAutorunApplicationMW.module.scss';

interface IManageAutorunApplicationMWProps {
  createApplication: (
    application: IAutorunApplication
  ) => Promise<FetchResult<ISetSettingResponse>>;
  updateApplication: (
    application: IAutorunApplication
  ) => Promise<FetchResult<ISetSettingResponse>>;
  deleteApplication: (
    application: IAutorunApplication
  ) => Promise<FetchResult<ISetSettingResponse>>;
  onClose: VoidFunction;
  application: IAutorunApplication | null;
}

const DEFAULT_VALUES: IAutorunApplication = {
  path: '',
  args: '',
  kill_on_seance: false,
  runas: false,
  run_hidden: false,
};

export const ManageAutorunApplicationMW: FC<IManageAutorunApplicationMWProps> =
  ({
    onClose,
    createApplication,
    updateApplication,
    deleteApplication,
    application,
  }) => {
    const { t } = useContext(LocalizationContext);

    const {
      control,
      handleSubmit,
      reset,
      formState: { isSubmitting, isDirty },
    } = useForm<IAutorunApplication>({
      defaultValues: { ...DEFAULT_VALUES },
    });

    const [isRemoveInProgress, setIsRemoveInProgress] = useState(false);

    useEffect(() => {
      if (application) {
        reset({ ...DEFAULT_VALUES, ...application });
      }
    }, [application]);

    return (
      <Modal onClose={onClose} disableOutsideClose>
        <form
          className={s.form}
          onSubmit={handleSubmit((values) => {
            if (application) {
              return updateApplication(values).then(onClose);
            }

            return createApplication(values).then(onClose);
          })}
        >
          <header className={s.header}>
            {t(
              application
                ? 'setting.shell.autorun.editing'
                : 'setting.shell.autorun.adding'
            )}
          </header>
          <Controller
            name="path"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormField title={t('setting.shell.autorun.path')} color="black">
                <Input
                  {...field}
                  variant="secondary"
                  placeholder={t('setting.shell.autorun.path.placeholder')}
                  error={error?.message}
                />
              </FormField>
            )}
            rules={{
              required: t('errors.required'),
              minLength: {
                value: 6,
                message: t('errors.minLength', 6),
              },
            }}
          />
          <Controller
            name="args"
            control={control}
            render={({ field }) => (
              <FormField title={t('setting.shell.autorun.args')} color="black">
                <Input
                  {...field}
                  variant="secondary"
                  placeholder={t('setting.shell.autorun.args.placeholder')}
                />
              </FormField>
            )}
          />
          <FormField title={t('setting.shell.autorun.rule')} color="black">
            <div className={s['togglers']}>
              <Controller
                name="kill_on_seance"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Toggle
                    value={value}
                    onChange={onChange}
                    label={t('setting.shell.autorun.rule.untilSession.label')}
                  />
                )}
              />
              <Controller
                name="runas"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Toggle
                    value={value}
                    onChange={onChange}
                    label={t('setting.shell.autorun.rule.always.label')}
                  />
                )}
              />
              <Controller
                name="run_hidden"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Toggle
                    value={value}
                    onChange={onChange}
                    label={t('setting.shell.autorun.rule.hidden.label')}
                  />
                )}
              />
            </div>
          </FormField>
          <div className={s.controls}>
            {application && (
              <Button
                variant="outline"
                color="red"
                onClick={() => {
                  setIsRemoveInProgress(true);

                  deleteApplication(application)
                    .then(onClose)
                    .finally(() => setIsRemoveInProgress(false));
                }}
                disabled={isSubmitting || isRemoveInProgress}
                fullWidth
              >
                {t('delete')}
              </Button>
            )}
            <Button
              type="submit"
              isLoading={isSubmitting}
              disabled={!isDirty || isRemoveInProgress}
              fullWidth
            >
              {t(application ? 'save' : 'create')}
            </Button>
          </div>
        </form>
      </Modal>
    );
  };
